import { useRouter } from "components/hooks";
import { Link } from "react-router-dom";

import BrandPublicProfileMagnetCard from "components/molecules/BrandPublicProfileMagnetCard";
import { useUserContext } from "components/context/UserContext";
import { getMagnetPageLink } from "utils/helpers";
import BlurredImage from "components/atoms/BlurredImage";
import MagnetPriceInfoWidget from "./MagnetPriceInfoWidget";
import BrandPublicProfileMementoCardHorizontal from "./BrandPublicProfileMementoCard";

type Props = {
  magnetImage: string;
  magnetName: string;
  release: {} | Release;
  magnetId: string;
  brandId: string;
  description: string;
  mementos: Memento[];
  isMobile?: boolean;
  isInteractionBlocked: boolean;
  magnetImageHash?: string | null;
  magnetType: MagnetProgramTypes;
};

function MagnetInfoCardWithMementos({
  magnetImage,
  magnetName,
  release,
  magnetId,
  brandId,
  description,
  mementos,
  isMobile,
  isInteractionBlocked,
  magnetImageHash,
  magnetType,
}: Props) {
  const { setCurrentlyViewingMementoId } = useUserContext();

  const router = useRouter();

  // const {
  //   handleNavigateToMagnetiqMagnetPublicProfile,
  //   walkthroughData: { run: isWalkThroughRunning },
  // } = useWalkthroughContext();

  const handleMementoClick = (inventoryId: string) => {
    setCurrentlyViewingMementoId(inventoryId);
  };

  const handleGoToMagnetPage = () => router.go(getMagnetPageLink(brandId, magnetId));

  return (
    <div>
      {isMobile ? (
        <div className="magnet-public-profile-card flex cursor-pointer flex-col gap-y-1 rounded-xl bg-white bg-opacity-0 transition duration-100 ease-linear hover:bg-opacity-5">
          {/* Magnet Image, Details and mementos */}
          <Link className=" grid grid-cols-12 gap-x-3 md:gap-x-5" to={getMagnetPageLink(brandId, magnetId)}>
            <div className="col-span-5">
              <div>
                <BlurredImage
                  src={magnetImage}
                  alt={magnetName}
                  imageContainerClasses="aspect-1 w-full rounded border border-white border-opacity-40
           object-cover object-center md:duration-500 md:ease-in-out md:hover:scale-105"
                  imageHash={magnetImageHash || null}
                />
              </div>
            </div>

            {/* Magnet Info and Mementos */}
            <div className="col-span-7 font-satoshi">
              <MagnetPriceInfoWidget
                isInteractionBlocked={isInteractionBlocked}
                release={release}
                magnetName={magnetName}
                magnetType={magnetType}
              />
            </div>
          </Link>

          <div className="flex flex-col font-satoshi">
            {/* Magnet Description */}
            <Link className="  text-white" to={getMagnetPageLink(brandId, magnetId)}>
              {/* <p className="text-[10px] font-bold lg:text-base">Magnet Description</p> */}
              <p className=" line-clamp-3  text-xxs font-medium opacity-60">{description}</p>
            </Link>
            {/* Mementos */}
            {mementos.length ? (
              <div className="mt-2 flex flex-col  gap-y-2">
                <p className="text-[10px] font-bold text-white">Mementos</p>

                <div className="secondary-scrollbar flex gap-x-2  overflow-x-scroll ">
                  {mementos.map((memento) => (
                    <div>
                      <BrandPublicProfileMementoCardHorizontal
                        endTime={memento.end_time}
                        actionPending={memento.action_pending}
                        key={memento.id}
                        name={memento.name}
                        imageUrl={memento.thumbnail_url as string}
                        mementoType={memento.activation_key}
                        isClickable
                        handleOnClick={() => handleMementoClick(memento.id)}
                      />
                    </div>
                  ))}
                </div>
                {/* ) : (
                <div className="my-4  flex flex-1 items-center justify-center font-satoshi  font-bold text-white">
                  <p>No Mementos Issued Yet!</p>
                </div>
              )} */}
              </div>
            ) : null}
          </div>
          <hr className="mt-5 text-white opacity-30" />
        </div>
      ) : (
        // Desktop
        <>
          <div
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                handleGoToMagnetPage();
              }
            }}
            role="link"
            onClick={handleGoToMagnetPage}
            className="magnet-public-profile-card grid cursor-pointer grid-cols-12 gap-x-2 rounded-2xl  p-3 transition duration-200 ease-linear hover:bg-[#061324]"
          >
            <div className="col-span-9 grid grid-cols-12">
              <div className="col-span-3">
                <BrandPublicProfileMagnetCard
                  onClick={handleGoToMagnetPage}
                  magnetImage={magnetImage}
                  magnetName={magnetName}
                />
              </div>
              <div className="col-span-9 flex flex-col justify-between px-6">
                <div className="">
                  <h2 className="mb-1 line-clamp-1 font-satoshi text-lg font-bold text-white ">{magnetName}</h2>
                  <p
                    title={description}
                    className=" line-clamp-3 cursor-default font-satoshi text-xxs font-450 leading-snug text-white text-opacity-60 "
                  >
                    {description}
                  </p>
                </div>
                <div>
                  <p className="my-2 font-satoshi text-xxs font-bold text-white 2xl:mb-4">Mementos</p>
                  {mementos.length ? (
                    <div className="secondary-scrollbar flex gap-x-5 overflow-x-scroll ">
                      {mementos.map((memento) => (
                        <div>
                          <BrandPublicProfileMementoCardHorizontal
                            endTime={memento.end_time}
                            actionPending={memento.action_pending}
                            key={memento.id}
                            name={memento.name}
                            imageUrl={memento.thumbnail_url as string}
                            mementoType={memento.activation_key}
                            isClickable
                            handleOnClick={() => handleMementoClick(memento.id)}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="my-6 flex  flex-1 items-center justify-center font-satoshi text-base font-bold text-white opacity-70 lg:my-8 xl:my-10  2xl:my-16">
                      <p>No Mementos Issued Yet!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-span-3 ">
              <MagnetPriceInfoWidget
                isInteractionBlocked={isInteractionBlocked}
                variant={1}
                magnetName={magnetName}
                release={release}
                magnetType={magnetType}
              />
            </div>
          </div>
          <hr className="mt-2 text-white opacity-30" />
        </>
      )}
    </div>
  );
}
export default MagnetInfoCardWithMementos;
